import { create } from 'zustand'
import { Database } from '@/types/supabase'

// Base type from Supabase schema
type CaregiverProfileData = Database['public']['CompositeTypes']['caregiver_profile_type'];
type SpecialistInfoData = Database['public']['CompositeTypes']['specialist_info_type'];

// Extended interface for frontend use
interface CaregiverProfile {
  id: string;
  name: string;
  auth_user_id: string;
  active: boolean;
  last_activity: string;
  last_activity_type: string | null;
}

// Comprehensive profile data with specialists
interface CaregiverFullProfile extends CaregiverProfile {
  email?: string | null;
  room_id?: string | null;
  specialists?: SpecialistInfoData[] | null;
  specialist_id?: string | null; // Add specialist_id for direct access
}

interface CaregiverStore {
  caregiverId: string | null;
  profile: CaregiverProfile | null;
  fullProfile: CaregiverFullProfile | null;
  setCaregiverId: (id: string | null) => void;
  setProfile: (profile: CaregiverProfile | null) => void;
  setFullProfile: (profile: CaregiverProfileData) => void;
  clearProfile: () => void;
}

export const useCaregiverStore = create<CaregiverStore>((set) => ({
  caregiverId: null,
  profile: null,
  fullProfile: null,
  setCaregiverId: (id) => set({ caregiverId: id }),
  setProfile: (profile) => set({ 
    profile,
    caregiverId: profile?.id ?? null 
  }),
  setFullProfile: (profileData) => {
    if (!profileData) return;
    
    // Map from RPC data to our frontend model
    const fullProfile: CaregiverFullProfile = {
      id: profileData.caregiver_id || '',
      name: profileData.caregiver_name || '',
      auth_user_id: profileData.caregiver_auth_user_id || '',
      active: profileData.is_active_caregiver || false,
      last_activity: '', // Not available in the RPC data
      last_activity_type: null,
      email: profileData.caregiver_email,
      specialists: profileData.specialists,
      room_id: profileData.specialists?.[0]?.room_id || null,
      // Extract specialist_id directly from the first specialist if available
      specialist_id: profileData.specialists?.[0]?.specialist_id || null,
    };
    
    set({ 
      fullProfile,
      caregiverId: fullProfile.id,
      // Also update basic profile for backward compatibility
      profile: {
        id: fullProfile.id,
        name: fullProfile.name,
        auth_user_id: fullProfile.auth_user_id,
        active: fullProfile.active,
        last_activity: fullProfile.last_activity,
        last_activity_type: fullProfile.last_activity_type
      }
    });
  },
  clearProfile: () => set({ 
    profile: null,
    fullProfile: null,
    caregiverId: null 
  })
}))